//eslint-disable-next-line @typescript-eslint/no-restricted-imports
import type { DialogProps as MUIDialogProps } from '@mui/material/Dialog';

//eslint-disable-next-line @typescript-eslint/no-restricted-imports
import MUIDialog from '@mui/material/Dialog';
import * as React from 'react';

import { DialogContext } from '@egr/xbox/egr-gui-dialogs/context/DialogContext';
import { SafeAreaBottomSpacer } from '@egr/xbox/egr-gui-elements/single-components/SafeAreaBottomSpacer';
import { getUUID } from '@egr/xbox/utils/Helper';
import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';
import { android } from '@egr/xbox/utils/UserAgent';

export type DialogProps = MUIDialogProps;

class DialogTracker extends SyncExternalStore<{
    openDialogs: Set<string>;
    openFullscreenDialogs: Set<string>;
}> {
    public useDialogOpen(): boolean {
        return this.useState(['openDialogs']).openDialogs.size > 0;
    }

    public get dialogOpen() {
        return this.snapshot.openDialogs.size > 0;
    }

    constructor() {
        super({
            openDialogs: new Set(),
            openFullscreenDialogs: new Set(),
        });
    }

    public updateDialogTracker(id: string, open: boolean, fullscreen: boolean): void {
        if (open) {
            this.updateState({
                openDialogs: new Set([...this.snapshot.openDialogs, id]),
                openFullscreenDialogs: fullscreen ? new Set([...this.snapshot.openFullscreenDialogs, id]) : this.snapshot.openFullscreenDialogs
            });
            return;
        }
        this.updateState({
            openDialogs: new Set([...this.snapshot.openDialogs].filter((value: string) => value !== id)),
            openFullscreenDialogs: new Set([...this.snapshot.openFullscreenDialogs].filter((value: string) => value !== id)),
        });
    }
}

export const dialogTracker = new DialogTracker();

export function useDialogTracker(open: boolean, fullscreen: boolean): void {
    const uuid = React.useRef(getUUID());
    React.useEffect(
        () => {
            dialogTracker.updateDialogTracker(uuid.current, open, fullscreen);
            return () => {
                dialogTracker.updateDialogTracker(uuid.current, false, false);
            };
        },
        [
            open,
            fullscreen
        ]
    );
}

export const MaxSizeDialogPaperProps = { sx: { width: '100%', height: '100%' }};

const Dialog: React.FC<DialogProps> = ({ children, ...dialogProps }) => {
    const uuid = React.useRef<string>(getUUID());
    React.useEffect(
        () => {
            dialogTracker.updateDialogTracker(uuid.current, dialogProps.open, dialogProps.fullScreen ?? false);
            return () => { dialogTracker.updateDialogTracker(uuid.current, false, false); };
        },
        [
            dialogProps.open,
            dialogProps.fullScreen,
            uuid,
        ]
    );
    return (
        <DialogContext.Provider
            value={{
                touchRipple: !android,
                fullscreen: dialogProps.fullScreen,
            }}
        >
            <MUIDialog {...dialogProps}>
                {children}
                {dialogProps.fullScreen ? <SafeAreaBottomSpacer/> : null}
            </MUIDialog>
        </DialogContext.Provider>
    );
};

export default Dialog;